import { PayloadAction } from '@reduxjs/toolkit';
import Config from 'Cloud/config';
import { logger } from 'lib/logger';
import { itemV4ToV2 } from 'reactApp/api/helpers/apiV4Helpers';
import { NewSearchApiCall } from 'reactApp/api/NewSearchApiCall';
import { SearchApiCall } from 'reactApp/api/SearchApiCall';
import { SearchHistoryClearApiCall, SearchHistoryDeleteApiCall, SearchHistoryLoadApiCall } from 'reactApp/api/SearchHistory';
import { VirusScanAPICall } from 'reactApp/api/VirusScanAPICall';
import { IS_PHONE_BROWSER } from 'reactApp/appHelpers/configHelpers';
import {
    isNewWebSearchHistoryEnabled,
    isTouchSearchContentEnabled,
    isTouchSearchHistoryEnabled,
    isTouchSearchNewBackendEnabled,
    isWebSearchContentEnabled,
    isWebSearchNewBackendEnabled,
} from 'reactApp/appHelpers/featuresHelpers';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { AttachesSelectors } from 'reactApp/modules/attaches/attaches.selectors';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { getSlicedSearch } from 'reactApp/modules/file/utils';
import { isThisOrParentMounted } from 'reactApp/modules/home/home.selectors';
import { renameItemSuccess } from 'reactApp/modules/modifying/modifying.actions';
import { getNewItem } from 'reactApp/modules/modifying/modifying.helpers';
import { IRenameItemSuccess } from 'reactApp/modules/modifying/modifying.types';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { IHistoryPush } from 'reactApp/modules/router/router.types';
import {
    searchFilterExactFolder,
    searchHistoryClear,
    searchHistoryRemoveItem,
    searchLoadError,
    searchLoadHistory,
    searchLoadHistoryError,
    searchLoadHistorySuccess,
    searchLoadInFolderRequest,
    searchLoadMoreRequest,
    searchLoadMoreSuccess,
    searchLoadRequest,
    searchLoadRequestStart,
    searchLoadStart,
    searchLoadSuccess,
    searchRequestParamsUpdate,
    searchUpdateDomainFolders,
    searchUpdateVirusScan,
} from 'reactApp/modules/search/search.module';
import { CloudItem, EStorageType } from 'reactApp/modules/storage/storage.types';
import { checkViewerAtIdChange, openMobileViewer as openMobileViewerAction } from 'reactApp/modules/viewer/viewer.module';
import { ESearchOptionType } from 'reactApp/ui/Header/Search/Search.data';
import { ESearchOptionSource } from 'reactApp/ui/WebSearch/WebSearch.data';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { decodeURIComponentHelper, getFilePathFromUrl } from 'reactApp/utils/urlHelper';
import { all, call, cancel, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { getDomainFoldersList } from '../domainFolders/domainFolders.saga';
import { SettingsSelectors } from '../settings/settings.selectors';
import { getLoadMoreLimit, getStorage } from '../storage/storage.helpers';
import { changeSearchHistory } from './search.actions';
import { sendSearchAnalytics, sendSearchStatistics, sendVirusScanStatistics } from './search.analytics';
import { getExtsBySearchType, SEARCH_PER_PAGE_LIMIT } from './search.helpers';
import { getSearchItemById, getSearchList, getSearchLoadMoreData, getSearchRequestParams } from './search.selectors';
import {
    ApiSearchResponceList,
    NewSearchApiRequestParams,
    SearchApiRequestParams,
    SearchFile,
    SearchHistoryGetApiRequestParams,
    SearchLoadParams,
    SearchRequestParams,
} from './search.types';

const DEPTH = 4294967295; // 2^32-1
const MAX_RESULT_COUNT = getLoadMoreLimit(EStorageType.search);
const MAX_NEW_RESULT_COUNT = 2000;
const SEARCH_HISTORY_LENGTH = 4;

const searchApiCall = (params: SearchApiRequestParams) => new SearchApiCall().makeRequest(params);
const newSearchApiCall = (params: SearchApiRequestParams) => new NewSearchApiCall().makeRequest(params);
const searchHistoryGetApiCall = (params: SearchHistoryGetApiRequestParams) => new SearchHistoryLoadApiCall().makeRequest(params);
const searchHistoryDeleteApiCall = (params: SearchHistoryGetApiRequestParams) => new SearchHistoryDeleteApiCall().makeRequest(params);
const searchHistoryClearApiCall = () => new SearchHistoryClearApiCall().makeRequest();
const virusScanApiCall = ({ hash_list }) => new VirusScanAPICall().makeRequest({ hash_list });

/** Загружаем доменные папки, находим их в результатах поиска и помечаем. */
export function* searchModifyingDomainFolders() {
    try {
        const domainFolders = yield call(getDomainFoldersList);
        const domainFoldersIDs = domainFolders?.list?.map((item) => item.home) || [];

        if (domainFoldersIDs.length) {
            yield put(searchUpdateDomainFolders(domainFoldersIDs));
        }
    } catch (error) {
        logger.error(error);
    }
}

/** Проверяем результаты поиска на вирусы и измениям поля 'virus_scan', 'blocked' и 'illegal'. */
export function* searchModifyingVirusScan(results: ApiSearchResponceList, offset: number, total: number) {
    try {
        const hashList = results.reduce<string[]>((acc, cur) => {
            if ('hash' in cur && cur?.hash) {
                acc.push(cur?.hash);
            }
            return acc;
        }, []);

        let apiRequestCallDeltaT = performance.now();

        const { data, status } = yield virusScanApiCall({ hash_list: hashList });
        apiRequestCallDeltaT = performance.now() - apiRequestCallDeltaT;
        sendVirusScanStatistics(status, apiRequestCallDeltaT, offset, total);

        const virusScanResults = data?.body || {};

        if (Object.keys(virusScanResults).length) {
            yield put(searchUpdateVirusScan(virusScanResults));
        }
    } catch (error) {
        logger.error(error);
    }
}

function* loadSearch(action: PayloadAction<SearchLoadParams>) {
    try {
        const { queryParam, type, folder, isAlbumsSearch = false } = action?.payload || {};
        yield put(searchLoadRequest());

        const isPhone = yield select(EnvironmentSelectors.isPhone);
        const queryParams = yield select(SettingsSelectors.getQueryParams);
        const query = queryParam || queryParams?.query || '';
        const searchType = type || queryParams?.searchType || ESearchOptionType.all;
        const searchSource = queryParams.searchSource || ESearchOptionSource.all;
        const extensions = getExtsBySearchType(searchType, isAlbumsSearch);
        const searchQuery = query.trim();
        const id = searchQuery || ROOT_FOLDER_ID;
        const { path } = yield select(getSearchRequestParams);
        const searchPath = folder || path || ROOT_FOLDER_ID;

        const searchFolder = searchType === ESearchOptionType.folder;
        const searchContent = searchSource === ESearchOptionSource.content;
        const newWebBackendEnabled = (isWebSearchNewBackendEnabled || isWebSearchContentEnabled) && !isPhone;
        const newTouchBackendEnabled = (isTouchSearchNewBackendEnabled || isTouchSearchContentEnabled) && isPhone;
        const searchOnlyName =
            (isTouchSearchNewBackendEnabled && !isTouchSearchContentEnabled) ||
            (isWebSearchNewBackendEnabled && !isWebSearchContentEnabled);
        const newApi = newWebBackendEnabled || newTouchBackendEnabled || isAlbumsSearch;
        let searchApi = searchApiCall;
        let params;
        if (newApi) {
            params = {
                query: searchQuery,
                path: searchPath,
                new_srch: true,
                srch_src: searchOnlyName ? ESearchOptionSource.name : searchSource,
                limit: MAX_NEW_RESULT_COUNT,
            } as NewSearchApiRequestParams;

            if (extensions) {
                params.exts = extensions;
            }

            if (searchFolder) {
                params.filesrch_type = 'folder';
            }

            searchApi = newSearchApiCall;
        } else {
            params = {
                q: searchQuery,
                path: searchPath,
                limit: MAX_RESULT_COUNT,
                // костыль для поиска папок - в новом бэкэнде не реализовано - должны ничего не находить при при поиске папок по содержимому
                depth: searchContent ? 0 : DEPTH,
            } as SearchApiRequestParams;

            if (extensions) {
                params.ext = extensions;
            }

            // Магический привет от API. "type=d" означает искать по типу "папка".
            if (searchFolder) {
                params.type = 'd';
            }
        }

        if (!extensions && !newApi && !searchFolder && id === ROOT_FOLDER_ID) {
            return yield put(searchLoadSuccess());
        }

        let apiRequestCallDeltaT = performance.now();

        const { status, headers, data } = yield searchApi(params);
        apiRequestCallDeltaT = performance.now() - apiRequestCallDeltaT;

        const results = (newApi ? data?.list?.map(itemV4ToV2) : data?.body?.list) || [];
        if (!isAlbumsSearch) {
            sendSearchStatistics(status, searchQuery, searchType, apiRequestCallDeltaT, results.length || 0);
        }

        yield put(searchLoadStart(results || []));

        // В альбомах если поиск идет по папке - нужно показывать файлы из строка заданной папки, не учитывая подпапки.
        if (isAlbumsSearch && searchPath !== ROOT_FOLDER_ID) {
            yield put(searchFilterExactFolder({ id: searchPath }));
        }

        // Сохраняем параметры для дальнейшего использования в других местах приложения.
        const xPageId = Config.get('x-page-id') || 'empty_header';
        const xReqId = headers?.['x-req-id'] || 'empty_header';
        const requestParams: SearchRequestParams = { xPageId, xReqId, query: searchQuery, path: params.path };

        if (searchType !== ESearchOptionType.all) {
            requestParams.type = searchType;
        }

        if (newApi && searchSource !== ESearchOptionSource.all) {
            requestParams.source = searchSource;
        }

        yield put(searchRequestParamsUpdate(requestParams));

        const place = IS_PHONE_BROWSER ? 'touch' : 'web';

        if (!isAlbumsSearch) {
            sendSearchAnalytics({ ...params, q: searchQuery, ext: extensions }, place, xPageId, xReqId, searchType);
        }

        if (results.length) {
            yield call(searchModifyingDomainFolders);
            // Сначала сканируем первые SEARCH_PER_PAGE_LIMIT файлов.
            const virusScanList = results.slice(0, SEARCH_PER_PAGE_LIMIT);
            yield call(searchModifyingVirusScan, virusScanList, 0, SEARCH_PER_PAGE_LIMIT);
        }
        yield put(searchLoadSuccess());

        const viewerItemId = decodeURIComponentHelper(getFilePathFromUrl(location?.pathname));

        if (viewerItemId && IS_PHONE_BROWSER) {
            yield put(openMobileViewerAction({ id: viewerItemId }));
        }
    } catch (error) {
        logger.error(error);
        yield put(searchLoadError());
    }
}

function* loadMoreSearch() {
    try {
        const { offset, list } = yield select(getSearchLoadMoreData);
        const nextResultList = list?.slice(offset, offset + SEARCH_PER_PAGE_LIMIT) || [];
        yield call(searchModifyingVirusScan, nextResultList, offset, list.length);

        yield put(searchLoadMoreSuccess(nextResultList.length));
    } catch (error) {
        logger.error(error);
        yield cancel();
    }
}

function* updateRenamedItems(id: string, newFolderId: string, oldFolderId: string) {
    const item: CloudItem = yield select((state) => getSearchItemById(state, id));

    if (!item) {
        return;
    }

    const newId = id.replace(oldFolderId, newFolderId);
    const isMounted: boolean = yield select(isThisOrParentMounted, item);
    const newItem: CloudItem = getNewItem({ item, newId, isMounted });

    yield put(renameItemSuccess({ newItem, oldId: item.id }));
}

// test
// -- test.jpg
// При переименовании папки test надо обновить test.jpg
function* handleRenameItems(action: PayloadAction<IRenameItemSuccess>) {
    const { newItem, oldId } = action.payload;

    const item = newItem as SearchFile;

    const storage = yield select(getCurrentStorage);
    const attachesSearchState = yield select(AttachesSelectors.getSearchState);
    const attachesQuery = attachesSearchState?.query;
    const { isSearch, isAttaches } = getStorage(storage);

    if (isSearch || (isAttaches && attachesQuery)) {
        const { xPageId, xReqId, query } = yield select(getSearchRequestParams);
        sendPaymentGa({
            eventCategory: ECategoryGa.search_results,
            action: 'rename',
            id_media: item.id,
            size_file: item.size,
            source: 'search',
            section: 'search',
            name_files: item.name,
            type_content: item.kind,
            search_phrase: query,
            page_id: xPageId,
            req_id: xReqId,
            pos: item.pos,
            hash: item.hash,
            placement: item.srchSrc,
        });
    }

    if (!newItem.isFolder) {
        return;
    }

    const searchList: string[] | undefined = yield select(getSearchList);
    const idsToUpdate = searchList?.filter((item) => item?.indexOf(`${oldId}/`) === 0);

    if (!idsToUpdate?.length) {
        return;
    }

    yield all(idsToUpdate.map((id: string) => updateRenamedItems(id, newItem.id, oldId)));
}

function* changeSearchHistorySaga(action: PayloadAction<IHistoryPush>) {
    if (!IS_PHONE_BROWSER) {
        return;
    }

    const { id } = action.payload;

    let idFixed;
    try {
        idFixed = decodeURIComponent(getSlicedSearch(id));
    } catch (error) {
        idFixed = getSlicedSearch(id);
        logger.error(error);
    }
    const item = yield select(getSearchItemById, idFixed);

    yield put(checkViewerAtIdChange({ id: item?.id, storage: EStorageType.search }));
}

function* removeSearchHistoryItem({ payload }: PayloadAction<string>) {
    const isPhone = yield select(EnvironmentSelectors.isPhone);

    if (isPhone ? !isTouchSearchHistoryEnabled : !isNewWebSearchHistoryEnabled) {
        return;
    }

    try {
        yield searchHistoryDeleteApiCall({ query: payload });
    } catch (error) {
        logger.error(error);
        yield put(searchLoadHistoryError());
    }
}

function* clearSearchHistory() {
    const isPhone = yield select(EnvironmentSelectors.isPhone);

    if (isPhone ? !isTouchSearchHistoryEnabled : !isNewWebSearchHistoryEnabled) {
        return;
    }

    try {
        yield searchHistoryClearApiCall();
    } catch (error) {
        logger.error(error);
        yield put(searchLoadHistoryError());
    }
}

function* loadSearchHistory() {
    const isPhone = yield select(EnvironmentSelectors.isPhone);

    if (isPhone ? !isTouchSearchHistoryEnabled : !isNewWebSearchHistoryEnabled) {
        return;
    }

    try {
        const {
            data: { history = [] },
        } = yield searchHistoryGetApiCall({ query: '' });
        const historyCount = history.slice(0, SEARCH_HISTORY_LENGTH);
        yield put(searchLoadHistorySuccess(historyCount));
    } catch (error) {
        logger.error(error);
        yield put(searchLoadHistoryError());
    }
}

function* loadSearchInFolder(action: PayloadAction<{ id: string }>) {
    const { id } = action.payload;
    const { query, type } = yield select(getSearchRequestParams);

    yield put(searchLoadRequestStart({ folder: id, queryParam: query, type, isAlbumsSearch: true }));
}

export function* watchSearch() {
    yield takeEvery(searchLoadRequestStart.toString(), loadSearch);
    yield takeEvery(searchLoadMoreRequest.toString(), loadMoreSearch);
    yield takeEvery(renameItemSuccess.toString(), handleRenameItems);
    yield takeEvery(searchLoadHistory.toString(), loadSearchHistory);
    yield takeEvery(searchHistoryRemoveItem.toString(), removeSearchHistoryItem);
    yield takeEvery(searchHistoryClear.toString(), clearSearchHistory);
    yield takeLatest(changeSearchHistory.toString(), changeSearchHistorySaga);
    yield takeEvery(searchLoadInFolderRequest.toString(), loadSearchInFolder);
}
